import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';

const GoogleOAuthCallback = () => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleGoogleCallback = () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        if (errorParam) {
          setError(decodeURIComponent(errorParam));
          setProcessing(false);
          return;
        }

        const token = urlParams.get('token');
        const userB64 = urlParams.get('user');
        const profileUrl = urlParams.get('profile_url') || '';
        const needsActivation = urlParams.get('needs_activation') === '1';

        if (!token || !userB64) {
          setError('Falta información de autenticación de Google');
          setProcessing(false);
          return;
        }

        // Decode base64 user payload
        const userJson = atob(userB64.replace(/-/g, '+').replace(/_/g, '/'));
        const userData = JSON.parse(decodeURIComponent(escape(userJson)));

        // Persist session
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        if (profileUrl) {
          localStorage.setItem('profile_url', profileUrl);
        }

        if (needsActivation) {
          localStorage.setItem(
            'google_activation_message',
            'Debes activar tu cuenta con el código de activación antes de poder usar la plataforma.'
          );
          navigate('/activate', { replace: true });
          return;
        }

        // Navigate to appropriate page — a pending onboarding draft means
        // this Google sign-in started from Step 5 of the registration
        // onboarding (see RegisterOnboardingPage), so we resume there
        // instead of going straight to the dashboard. The onboarding page
        // itself reads and consumes the draft from sessionStorage.
        const hasOnboardingDraft = !!sessionStorage.getItem('cn_onboarding_draft');
        if (userData.is_admin) {
          navigate('/admin', { replace: true });
        } else if (hasOnboardingDraft) {
          navigate('/register', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }

        // Force a reload so AuthContext picks up the new token
        setTimeout(() => window.location.reload(), 50);
      } catch (e) {
        console.error('Google OAuth callback error:', e);
        setError('Error al procesar la autenticación con Google');
        setProcessing(false);
      }
    };

    handleGoogleCallback();
  }, [navigate]);

  if (processing) {
    return (
      <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-testid="google-oauth-processing">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <ContactLogo className="h-12 w-auto mx-auto" />
            <h2 className="mt-6 text-2xl font-bold text-black">
              Procesando inicio de sesión con Google...
            </h2>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-contact flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-testid="google-oauth-error">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <ContactLogo className="h-12 w-auto mx-auto" />
          <h2 className="mt-6 text-2xl font-bold text-black">
            Error de autenticación
          </h2>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            data-testid="back-to-login-btn"
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-sky-300 text-white rounded-lg hover:bg-sky-400"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
